import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Col, FormLabel, Row, Alert, Form } from 'react-bootstrap';
import InputText from '../../components/Forms/Element/InputText';
import InputDate from '../../components/Forms/Element/InputDate';
import InputDecimal from '../../components/Forms/Element/InputDecimal';
import InputNumeric from '../../components/Forms/Element/InputNumeric';
import TextArea from '../../components/Forms/Element/TextArea';
import CustomSelect from '../../components/Forms/Element/CustomSelect';
import CustomerSelect from '../../components/Custom/Main/Customer/CustomerSelect';
import EmployeeSelect from '../../components/Custom/Main/Employee/EmployeeSelect';
import ProductSelect from '../../components/Custom/Main/Product/ProductSelect';
import axiosInstance from '../../../services/AxiosInstance';
import Api from '../../../constants/Api';
import MenuConfig from '../../../constants/MenuConfig';
import { errorAlert, errorObjectAlert, successAlert, warnAlert } from '../../components/alert/defaultAlert';
import NewPage from '../../layouts/page/NewPage';
import AsteriskRequired from '../../components/Common/AsteriskRequired';
import { toLocalISOString } from '../../../utils/DateUtils';
import BranchSelect from '../../components/Custom/Main/Branch/BranchSelect';
import AppointmentStatus from '../../components/Custom/Appointment/AppointmentStatus';
import PlanOffDayNotice from '../../components/Custom/Subscription/Plan/PlanOffDayNotice';
import { convertStringToStringDouble } from '../../../utils/ConverterUtils';
import { useBranch } from '../../../context/BranchContext';
import { worksOnDate } from '../../../utils/WorkDayUtils';
import { isOnVacationOn, vacationMessage } from '../../../utils/VacationUtils';
import {
    AppointmentDepositDraftSection,
    buildDepositDraft,
    registerDeposit,
    requiresDeposit,
    validateDepositDraft
} from '../../components/Custom/Appointment/AppointmentDeposit';
import './AppointmentNew.css';

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120];
const DEFAULT_APPOINTMENT_DURATION = 60;
const DAY_OF_WEEK_KEYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const ALL_STATUS_OPTIONS = [
    { value: AppointmentStatus.SCHEDULED, label: 'Agendado' },
    { value: AppointmentStatus.CONFIRMED, label: 'Confirmado' },
    { value: AppointmentStatus.IN_PROGRESS, label: 'Em Andamento' },
    { value: AppointmentStatus.COMPLETED, label: 'Concluído' },
    { value: AppointmentStatus.CANCELED, label: 'Cancelado' },
    { value: AppointmentStatus.NO_SHOW, label: 'Não Compareceu' }
];

const CURRENCY_FORMATTER = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
});

const roundToNextQuarter = (date) => {
    const result = new Date(date);
    result.setSeconds(0, 0);

    const minutes = result.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;

    if (roundedMinutes === 60) {
        result.setHours(result.getHours() + 1, 0, 0, 0);
        return result;
    }

    result.setMinutes(roundedMinutes);
    return result;
};

const getTimeValue = (date) => {
    if (!date) return '';
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
};

const combineDateAndTime = (date, time) => {
    if (!date || !time) return null;

    const [hours, minutes] = time.split(':').map(Number);
    const combined = new Date(date);
    combined.setHours(hours || 0, minutes || 0, 0, 0);
    return combined;
};

const addMinutes = (date, minutes) => {
    if (!date || !Number.isFinite(minutes)) return null;

    return new Date(date.getTime() + (minutes * 60 * 1000));
};

const parseDurationMinutes = (value) => {
    if (value === '' || value === null || value === undefined) return null;

    const parsedValue = parseInt(value, 10);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
        return null;
    }

    return parsedValue;
};

const resolveDurationMinutes = (value) => parseDurationMinutes(value) || DEFAULT_APPOINTMENT_DURATION;

const isQuarterHourTime = (time) => {
    if (!time) return false;

    const [hours, minutes] = time.split(':').map(Number);
    return Number.isInteger(hours) && Number.isInteger(minutes) && minutes % 15 === 0;
};

const getAppointmentStartDate = (appointment) => {
    if (!appointment?.scheduledDate) return null;

    const startDate = new Date(appointment.scheduledDate);
    return Number.isNaN(startDate.getTime()) ? null : startDate;
};

const getAppointmentEndDate = (appointment) => {
    const startDate = getAppointmentStartDate(appointment);
    if (!startDate) return null;

    return addMinutes(startDate, resolveDurationMinutes(appointment?.duration));
};

const findAppointmentConflict = (appointments, startDate, endDate, currentAppointmentId = null) => {
    if (!startDate || !endDate) return null;

    return appointments.find((appointment) => {
        if (!appointment) return false;
        if (currentAppointmentId && String(appointment.id) === String(currentAppointmentId)) return false;
        if (appointment.appointmentStatus === AppointmentStatus.CANCELED) return false;

        const appointmentStart = getAppointmentStartDate(appointment);
        const appointmentEnd = getAppointmentEndDate(appointment);

        if (!appointmentStart || !appointmentEnd) return false;

        return appointmentStart < endDate && appointmentEnd > startDate;
    }) || null;
};

const formatTimeLabel = (date) => {
    if (!date) return '--:--';

    return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
    });
};

const buildOption = (entity, fallbackLabel) => {
    if (!entity?.id) return null;

    return {
        value: entity.id,
        label: fallbackLabel
    };
};

const formatPreviewDateTime = (date, time) => {
    if (!date) return 'Data e horário não definidos';

    const formattedDate = date.toLocaleDateString('pt-BR', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });

    return time ? `${formattedDate} às ${time}` : formattedDate;
};

const formatPreviewPrice = (value) => {
    const normalizedValue = convertStringToStringDouble(value);
    if (!normalizedValue) return 'A definir';

    const parsedValue = Number(normalizedValue);
    if (!Number.isFinite(parsedValue)) return 'A definir';

    return CURRENCY_FORMATTER.format(parsedValue);
};

const getSummaryValue = (option, fallback) => option?.label || fallback;

const getDayOfWeekKey = (date) => {
    if (!date) return null;
    return DAY_OF_WEEK_KEYS[date.getDay()] || null;
};

const toMinutesOfDay = (value) => {
    if (!value) return null;

    const [hours, minutes] = value.split(':').map(Number);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
        return null;
    }

    return (hours * 60) + minutes;
};

const formatHourLabel = (value) => value ? value.slice(0, 5) : '--:--';

const validateAgainstWorkingHours = (workingHoursList, startDate, endDate) => {
    const dayOfWeekKey = getDayOfWeekKey(startDate);
    const workingDay = Array.isArray(workingHoursList)
        ? workingHoursList.find((item) => item.dayOfWeek === dayOfWeekKey)
        : null;

    if (!workingDay) {
        return null;
    }

    if (!workingDay.isOpen) {
        return 'A filial selecionada está fechada na data informada.';
    }

    const startMinutes = (startDate.getHours() * 60) + startDate.getMinutes();
    const endMinutes = (endDate.getHours() * 60) + endDate.getMinutes();
    const openMinutes = toMinutesOfDay(workingDay.openTime);
    const closeMinutes = toMinutesOfDay(workingDay.closeTime);

    if (openMinutes !== null && closeMinutes !== null && (startMinutes < openMinutes || endMinutes > closeMinutes)) {
        return `O agendamento deve estar dentro do horário de funcionamento da filial: ${formatHourLabel(workingDay.openTime)} às ${formatHourLabel(workingDay.closeTime)}.`;
    }

    const breakStartMinutes = toMinutesOfDay(workingDay.breakStartTime);
    const breakEndMinutes = toMinutesOfDay(workingDay.breakEndTime);
    if (breakStartMinutes !== null && breakEndMinutes !== null) {
        const overlapsBreak = startMinutes < breakEndMinutes && endMinutes > breakStartMinutes;
        if (overlapsBreak) {
            return `O agendamento não pode sobrepor o intervalo da filial: ${formatHourLabel(workingDay.breakStartTime)} às ${formatHourLabel(workingDay.breakEndTime)}.`;
        }
    }

    return null;
};

const AppointmentNew = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedBranch } = useBranch();
    // Prefill de encaixe (vindo do botão "Encaixar neste horário" no detalhe do agendamento).
    const encaixePrefill = location.state?.encaixe || null;
    // Guarda o valor anterior da filial para só zerar o profissional quando o usuário TROCA de
    // filial — não na primeira definição (carga inicial / prefill de encaixe), preservando o
    // profissional pré-preenchido.
    const prevBranchValueRef = useRef(undefined);
    // Trava de envio. O botão só é desabilitado por showLoading, que entra depois das duas
    // consultas que o submit faz antes de gravar (expediente da filial e agenda do dia). Nessa
    // espera o botão segue clicável e a recepção, sem retorno na tela, clicava de novo — o que
    // gerava dois cadastros. Ref, e não state, porque precisa valer já no clique seguinte, sem
    // esperar o próximo render.
    const isSubmittingRef = useRef(false);
    const [showLoading, setShowLoading] = useState(false);

    const [uuid, setUuid] = useState('');
    const [customer, setCustomer] = useState(null);
    const [employee, setEmployee] = useState(null);
    const [service, setService] = useState(null);
    // Reserva a receber junto com o agendamento novo; só aparece quando o serviço a exige.
    const [deposit, setDeposit] = useState(buildDepositDraft());
    const [appointmentStatus, setAppointmentStatus] = useState({ value: 'SCHEDULED', label: 'Agendado' });
    const [appointmentDate, setAppointmentDate] = useState(null);
    const [appointmentTime, setAppointmentTime] = useState('');
    const [durationMinutes, setDurationMinutes] = useState('');
    const [price, setPrice] = useState('');
    const [notes, setNotes] = useState('');
    const [branch, setBranch] = useState(null);
    const [cancellationReason, setCancellationReason] = useState('');
    // Encaixe: quando marcado, a recepção assume a sobreposição e ignoramos o bloqueio de conflito.
    const [isEncaixe, setIsEncaixe] = useState(false);
    const [employeeDayAppointments, setEmployeeDayAppointments] = useState([]);
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

    const isEditing = Boolean(id);
    // Somente leitura de propósito: a correção de um atendimento acontece no painel da agenda,
    // que é onde o operador trabalha. Esta tela existe apenas para consulta de um agendamento
    // específico, e não deve virar um segundo caminho de edição.
    const isReadOnly = isEditing;

    // A seção de reserva trabalha com a lista de serviços do agendamento rápido; aqui há um só.
    const depositServiceOptions = useMemo(() => (service ? [{ value: service.value, service }] : []), [service]);
    // A sugestão acompanha o serviço escolhido.
    useEffect(() => {
        setDeposit(buildDepositDraft(service ? [{ service }] : []));
    }, [service]);

    const generateUUID = () => {
        return `APP-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    };

    const loadAppointment = useCallback(() => {
        setShowLoading(true);
        axiosInstance.get(`${Api.appointment.appointment}/${id}`)
            .then((response) => {
                const data = response.data;
                const loadedDate = data.scheduledDate ? new Date(data.scheduledDate) : null;

                setUuid(data.uuid || '');
                setCustomer(buildOption(data.customer, `${data.customer?.firstName || ''} ${data.customer?.lastName || ''} (${data.customer?.phone || ''})`.trim()));
                setEmployee(buildOption(data.employee, data.employee?.fullName || data.employee?.name || ''));
                setService(buildOption(data.service, data.service?.name || data.service?.description || ''));
                setAppointmentStatus(ALL_STATUS_OPTIONS.find((status) => status.value === data.appointmentStatus) || ALL_STATUS_OPTIONS[0]);
                setAppointmentDate(loadedDate);
                setAppointmentTime(getTimeValue(loadedDate));
                setDurationMinutes(data.duration || '');
                setPrice(data.price || '');
                setNotes(data.notes || '');
                setIsEncaixe(Boolean(data.encaixe));
                setCancellationReason(data.cancellationReason || '');
                setBranch(buildOption(data.branch, data.branch?.name || ''));
            })
            .catch((error) => errorAlert(error))
            .finally(() => {
                setShowLoading(false);
            });
    }, [id]);

    useEffect(() => {
        if (id) {
            loadAppointment();
            return;
        }

        setUuid(generateUUID());

        // Encaixe: pré-preenche mesmo horário/profissional/filial do atendimento de origem
        // e já marca "Encaixe", para lançar um cliente por cima desse horário.
        if (encaixePrefill) {
            const prefilledDate = encaixePrefill.scheduledDate ? new Date(encaixePrefill.scheduledDate) : roundToNextQuarter(new Date());
            setAppointmentDate(prefilledDate);
            setAppointmentTime(getTimeValue(prefilledDate));
            setDurationMinutes(encaixePrefill.duration ? String(encaixePrefill.duration) : String(DEFAULT_APPOINTMENT_DURATION));
            setIsEncaixe(true);
            if (encaixePrefill.branch) {
                setBranch(buildOption(encaixePrefill.branch, encaixePrefill.branch.name || ''));
            }
            if (encaixePrefill.employee) {
                setEmployee(buildOption(encaixePrefill.employee, encaixePrefill.employee.fullName || encaixePrefill.employee.name || ''));
            }
            return;
        }

        const nextQuarter = roundToNextQuarter(new Date());
        setAppointmentDate(nextQuarter);
        setAppointmentTime(getTimeValue(nextQuarter));
        setDurationMinutes(String(DEFAULT_APPOINTMENT_DURATION));
    }, [id, loadAppointment, encaixePrefill]);

    useEffect(() => {
        // No prefill de encaixe a filial já vem definida; não deixamos o padrão do contexto sobrescrevê-la.
        if (!id && !encaixePrefill?.branch && selectedBranch && !branch) {
            setBranch({
                value: selectedBranch.id,
                label: selectedBranch.name
            });
        }
    }, [selectedBranch, branch, id, encaixePrefill]);

    useEffect(() => {
        if (isReadOnly) {
            return;
        }

        const currentBranchValue = branch?.value;
        // Só limpamos o profissional quando o usuário TROCA de filial (valor anterior já definido).
        // Na primeira definição (carga inicial / prefill de encaixe) preservamos o profissional.
        if (prevBranchValueRef.current !== undefined && prevBranchValueRef.current !== currentBranchValue) {
            setEmployee(null);
        }
        prevBranchValueRef.current = currentBranchValue;
    }, [branch?.value, isReadOnly]);

    useEffect(() => {
        if (appointmentStatus?.value !== AppointmentStatus.CANCELED && cancellationReason) {
            setCancellationReason('');
        }
    }, [appointmentStatus, cancellationReason]);

    const fetchEmployeeDayAppointments = useCallback(async (selectedEmployee, selectedDate) => {
        if (!selectedEmployee?.value || !selectedDate || isReadOnly) {
            return [];
        }

        const startDate = new Date(selectedDate);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(selectedDate);
        endDate.setHours(23, 59, 59, 999);

        const response = await axiosInstance.get(Api.appointment.schedule, {
            params: {
                startDate: toLocalISOString(startDate),
                endDate: toLocalISOString(endDate)
            }
        });

        return (response.data || []).filter(
            (appointment) => String(appointment.employee?.id || '') === String(selectedEmployee.value)
        );
    }, [isReadOnly]);

    useEffect(() => {
        if (!employee?.value || !appointmentDate || isReadOnly) {
            setEmployeeDayAppointments([]);
            return;
        }

        let isMounted = true;
        setIsCheckingAvailability(true);

        fetchEmployeeDayAppointments(employee, appointmentDate)
            .then((appointments) => {
                if (isMounted) {
                    setEmployeeDayAppointments(appointments);
                }
            })
            .catch((error) => {
                console.error('Error loading employee appointments for validation:', error);
                if (isMounted) {
                    setEmployeeDayAppointments([]);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsCheckingAvailability(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [employee, appointmentDate, isReadOnly, fetchEmployeeDayAppointments]);

    const normalizedDurationMinutes = useMemo(() => parseDurationMinutes(durationMinutes), [durationMinutes]);
    const effectiveDurationMinutes = useMemo(() => resolveDurationMinutes(durationMinutes), [durationMinutes]);
    const scheduledStartDate = useMemo(() => combineDateAndTime(appointmentDate, appointmentTime), [appointmentDate, appointmentTime]);
    const scheduledEndDate = useMemo(
        () => addMinutes(scheduledStartDate, effectiveDurationMinutes),
        [scheduledStartDate, effectiveDurationMinutes]
    );

    const conflictingAppointment = useMemo(
        () => (employee?.value && scheduledStartDate && scheduledEndDate
            ? findAppointmentConflict(employeeDayAppointments, scheduledStartDate, scheduledEndDate, id)
            : null),
        [employee, scheduledStartDate, scheduledEndDate, employeeDayAppointments, id]
    );

    // Indicador visual do dia de folga; o envio ignora essa restrição quando a recepção
    // marcou o lançamento como encaixe.
    const isEmployeeDayOff = useMemo(
        () => Boolean(employee?.value && scheduledStartDate && !worksOnDate(employee.workDays, scheduledStartDate)),
        [employee, scheduledStartDate]
    );

    // Férias não entram na exceção do encaixe: o backend recusa de qualquer forma, porque
    // quem está de férias não está na loja para ser encaixado.
    const isEmployeeOnVacation = useMemo(
        () => Boolean(employee?.value && scheduledStartDate && isOnVacationOn(employee, scheduledStartDate)),
        [employee, scheduledStartDate]
    );

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Um envio por vez: o segundo clique é descartado em vez de virar outro cadastro.
        if (isSubmittingRef.current) {
            return;
        }

        isSubmittingRef.current = true;

        try {
            await submitAppointment();
        } finally {
            isSubmittingRef.current = false;
        }
    };

    const submitAppointment = async () => {
        if (isReadOnly) {
            return;
        }

        if (!customer) {
            warnAlert(t('validation.required.customer'));
            return;
        }

        if (!appointmentDate) {
            warnAlert(t('validation.required.appointment_date'));
            return;
        }

        if (!appointmentTime) {
            warnAlert('Informe o horário do agendamento.');
            return;
        }

        if (!isQuarterHourTime(appointmentTime)) {
            warnAlert('Use horários em intervalos de 15 minutos para manter a agenda consistente.');
            return;
        }

        if (!branch) {
            warnAlert(t('validation.required.branch'));
            return;
        }

        if (durationMinutes && !normalizedDurationMinutes) {
            warnAlert('Informe uma duração válida em minutos.');
            return;
        }

        if (!appointmentStatus?.value) {
            warnAlert(t('validation.required.status'));
            return;
        }

        if (appointmentStatus?.value === AppointmentStatus.CANCELED && !cancellationReason.trim()) {
            warnAlert(t('validation.required.cancellation_reason'));
            return;
        }

        const scheduledDate = combineDateAndTime(appointmentDate, appointmentTime);

        if (!scheduledDate) {
            warnAlert('Não foi possível montar a data e hora do agendamento.');
            return;
        }

        const endDate = addMinutes(scheduledDate, effectiveDurationMinutes);

        if (!endDate || endDate <= scheduledDate) {
            warnAlert('A duração informada precisa gerar um horário final válido.');
            return;
        }

        if (employee?.value && isOnVacationOn(employee, scheduledDate)) {
            warnAlert(`${vacationMessage(employee, employee.label)} Escolha outra data ou outro profissional.`);
            return;
        }

        // No fluxo comum o backend recusa esse agendamento; encaixe é a exceção operacional
        // explícita e pode ser lançado mesmo quando o profissional está de folga.
        if (!isEncaixe && employee?.value && !worksOnDate(employee.workDays, scheduledDate)) {
            warnAlert(`${employee.label} não atende neste dia da semana. Escolha outra data ou outro profissional.`);
            return;
        }

        if (!isEncaixe) {
            try {
                const workingHoursResponse = await axiosInstance.get(`${Api.main.branch_working_hours}/branch/${branch.value}`);
                const workingHoursMessage = validateAgainstWorkingHours(workingHoursResponse.data || [], scheduledDate, endDate);

                if (workingHoursMessage) {
                    warnAlert(workingHoursMessage);
                    return;
                }
            } catch (error) {
                errorAlert(error);
                return;
            }
        }

        let appointmentsToValidate = [];

        if (employee?.value) {
            setIsCheckingAvailability(true);

            try {
                appointmentsToValidate = await fetchEmployeeDayAppointments(employee, appointmentDate);
                setEmployeeDayAppointments(appointmentsToValidate);
            } catch (error) {
                setIsCheckingAvailability(false);
                errorAlert(error);
                return;
            }

            setIsCheckingAvailability(false);

            const overlappingAppointment = findAppointmentConflict(appointmentsToValidate, scheduledDate, endDate, id);

            // Com encaixe marcado, a recepção assume a sobreposição de propósito — não bloqueamos o conflito.
            if (overlappingAppointment && !isEncaixe) {
                warnAlert(`Este horário conflita com outro atendimento do profissional entre ${formatTimeLabel(getAppointmentStartDate(overlappingAppointment))} e ${formatTimeLabel(getAppointmentEndDate(overlappingAppointment))}. Marque "Encaixe" para agendar mesmo assim.`);
                return;
            }
        }

        // Reserva só na criação; edição é somente consulta (ver isReadOnly) e a reserva de um
        // agendamento existente se trata pelo detalhe na agenda.
        if (!id && requiresDeposit(depositServiceOptions)) {
            const depositProblem = validateDepositDraft(deposit, convertStringToStringDouble(price));
            if (depositProblem) {
                warnAlert(depositProblem);
                return;
            }
        }

        const payload = {
            uuid,
            customer: { id: customer.value },
            employee: employee ? { id: employee.value } : null,
            service: service ? { id: service.value } : null,
            appointmentStatus: appointmentStatus.value,
            scheduledDate: toLocalISOString(scheduledDate),
            duration: effectiveDurationMinutes,
            price: convertStringToStringDouble(price) || null,
            notes: notes || null,
            encaixe: isEncaixe,
            branch: branch ? { id: branch.value } : null,
            cancellationReason: appointmentStatus?.value === AppointmentStatus.CANCELED ? cancellationReason || null : null
        };

        setShowLoading(true);

        const request = id
            ? axiosInstance.put(`${Api.appointment.appointment}/${id}`, payload)
            : axiosInstance.post(Api.appointment.appointment, payload);

        // Aguardado para a trava de envio só cair depois que a gravação termina.
        await request
            .then(async (response) => {
                // O agendamento já existe neste ponto. Se a reserva falhar, avisa em vez de
                // desfazer: o horário continua marcado e a reserva se recebe pelo detalhe.
                let depositProblem = null;
                if (!id && deposit.receiveNow && requiresDeposit(depositServiceOptions) && response.data?.id) {
                    depositProblem = await registerDeposit(response.data.id, deposit)
                        .then(() => null)
                        .catch((error) => error.response?.data?.message || error.message);
                }
                successAlert(t('message.operation_successfully'), () => {
                    navigate(MenuConfig.appointment.appointment.route);
                });
                if (depositProblem) {
                    warnAlert(`A reserva não foi registrada: ${depositProblem}. Receba pelo detalhe do agendamento.`);
                }
            })
            .catch((error) => errorObjectAlert(error))
            .finally(() => {
                setShowLoading(false);
            });
    };

    const summaryStatusClass = useMemo(() => {
        switch (appointmentStatus?.value) {
            case AppointmentStatus.CANCELED:
                return 'is-danger';
            case AppointmentStatus.CONFIRMED:
            case AppointmentStatus.COMPLETED:
                return 'is-success';
            case AppointmentStatus.IN_PROGRESS:
                return 'is-warning';
            default:
                return 'is-primary';
        }
    }, [appointmentStatus]);

    const appointmentFormContent = useMemo(() => (
        <div className="appointment-form form-layout form-layout--spaced">
            <div className="appointment-form__hero">
                <div>
                    <span className="appointment-form__eyebrow">
                        {isEditing ? 'Consulta do atendimento' : 'Organize o novo atendimento'}
                    </span>
                    <h4 className="appointment-form__hero-title mb-2">
                        {isEditing ? 'Visualizar agendamento' : 'Novo agendamento'}
                    </h4>
                    <p className="appointment-form__hero-copy mb-0">
                        {isEditing
                            ? 'Somente consulta. Para corrigir dados, mudar horário ou alterar o status, use a agenda: é lá que estão todas as ações do atendimento.'
                            : 'Centralize cliente, agenda e detalhes do serviço em uma única visão, com um resumo lateral para revisão antes de salvar.'}
                    </p>
                </div>

                <div className="appointment-form__hero-meta">
                    <div className="appointment-form__meta-chip">
                        <span>Status</span>
                        <strong>{appointmentStatus?.label || 'Agendado'}</strong>
                    </div>
                    <div className="appointment-form__meta-chip">
                        <span>Agenda</span>
                        <strong>{appointmentDate ? appointmentTime || 'Definir horário' : 'Definir data'}</strong>
                    </div>
                </div>
            </div>

            {!isEditing && (
                <Alert variant="info" className="appointment-form__info">
                    Preencha cliente, data e horário primeiro. Serviço, profissional, preço e observações podem ser ajustados depois sem atrapalhar o fluxo.
                </Alert>
            )}

            {isReadOnly && (
                <Alert variant="info" className="mb-4">
                    <div className="d-flex align-items-start">
                        <i className="fas fa-info-circle me-3 mt-1" style={{ fontSize: '1.2rem' }}></i>
                        <div>
                            <h6 className="alert-heading mb-2">Modo de visualização</h6>
                            <p className="mb-0">
                                Este agendamento está bloqueado para edição nesta tela. Para alterar o status, use a ação própria na lista de agendamentos.
                            </p>
                        </div>
                    </div>
                </Alert>
            )}

            <Row className="g-4">
                <Col xl={8} lg={7}>
                    <div className="appointment-form__panel form-layout__panel">
                        <div className="form-layout__panel-header">
                            <div>
                                <h5 className="mb-1">Dados principais</h5>
                                <p className="mb-0">Identifique cliente, filial e contexto do agendamento.</p>
                            </div>
                        </div>

                        <Row className="g-3">
                            <Col xl={6} md={12}>
                                <FormLabel>{t('customer')} <AsteriskRequired /></FormLabel>
                                <CustomerSelect
                                    value={customer}
                                    onChange={(item) => setCustomer(item)}
                                    isDisabled={isReadOnly}
                                    crossBranch
                                />
                            </Col>

                            <Col xl={3} md={6}>
                                <FormLabel>{t('branch')} <AsteriskRequired /></FormLabel>
                                <BranchSelect
                                    name="branch"
                                    value={branch}
                                    onChange={(value) => setBranch(value)}
                                    isDisabled={isReadOnly || (!isEditing && !!selectedBranch)}
                                />
                            </Col>

                            <Col xl={3} md={6}>
                                <FormLabel>{isEditing ? t('status') : 'Status inicial'} <AsteriskRequired /></FormLabel>
                                {isEditing ? (
                                    <InputText value={appointmentStatus?.label || ''} disabled />
                                ) : (
                                    <CustomSelect
                                        value={appointmentStatus}
                                        options={ALL_STATUS_OPTIONS}
                                        onChange={(status) => setAppointmentStatus(status || ALL_STATUS_OPTIONS[0])}
                                        isClearable={false}
                                        placeholder={t('select')}
                                    />
                                )}
                            </Col>
                        </Row>
                    </div>

                    <div className="appointment-form__panel form-layout__panel">
                        <div className="form-layout__panel-header">
                            <div>
                                <h5 className="mb-1">Agenda e valores</h5>
                                <p className="mb-0">Defina quando o atendimento acontece e quanto tempo ele deve ocupar.</p>
                            </div>
                        </div>

                        <Row className="g-3">
                            <Col xl={4} md={6}>
                                <FormLabel>Data <AsteriskRequired /></FormLabel>
                                <InputDate
                                    selected={appointmentDate}
                                    onChange={(date) => setAppointmentDate(date)}
                                    disabled={isReadOnly}
                                />
                            </Col>

                            <Col xl={3} md={6}>
                                <FormLabel>Horário <AsteriskRequired /></FormLabel>
                                <input
                                    type="time"
                                    className="form-control input-default appointment-form__time-input"
                                    value={appointmentTime}
                                    step="900"
                                    disabled={isReadOnly}
                                    onChange={(event) => setAppointmentTime(event.target.value)}
                                />
                            </Col>

                            <Col xl={2} md={6}>
                                <FormLabel>{t('duration_minutes')}</FormLabel>
                                <InputNumeric
                                    value={durationMinutes}
                                    disabled={isReadOnly}
                                    onChange={(event) => setDurationMinutes(event.target.value)}
                                />
                            </Col>

                            <Col xl={3} md={6}>
                                <FormLabel>{t('price')}</FormLabel>
                                <InputDecimal
                                    value={price}
                                    disabled={isReadOnly}
                                    onChange={(event) => setPrice(event.target.value)}
                                />
                            </Col>
                        </Row>

                        <div className="appointment-form__duration-block">
                            <div className="appointment-form__duration-header">
                                <FormLabel className="mb-0">Sugestões rápidas de duração</FormLabel>
                                <span className="appointment-form__duration-help">Use um atalho para preencher mais rápido. Se não informar, o sistema assume 60 minutos.</span>
                            </div>
                            <div className="appointment-form__duration-presets">
                                {DURATION_PRESETS.map((preset) => (
                                    <button
                                        key={preset}
                                        type="button"
                                        disabled={isReadOnly}
                                        className={`appointment-form__duration-chip ${String(durationMinutes) === String(preset) ? 'is-active' : ''} ${isReadOnly ? 'is-disabled' : ''}`}
                                        onClick={() => {
                                            if (!isReadOnly) {
                                                setDurationMinutes(String(preset));
                                            }
                                        }}
                                    >
                                        {preset} min
                                    </button>
                                ))}
                            </div>
                        </div>

                        <Alert
                            variant={isEmployeeOnVacation || isEmployeeDayOff ? 'danger' : conflictingAppointment ? (isEncaixe ? 'warning' : 'danger') : 'secondary'}
                            className={`appointment-form__interval-alert mb-0 mt-3 ${conflictingAppointment || isEmployeeDayOff || isEmployeeOnVacation ? 'is-conflict' : 'is-neutral'}`}
                        >
                            <strong>Intervalo previsto:</strong>{' '}
                            {scheduledStartDate && scheduledEndDate
                                ? <span className="appointment-form__interval-value">{`${formatTimeLabel(scheduledStartDate)} - ${formatTimeLabel(scheduledEndDate)} (${effectiveDurationMinutes} min)`}</span>
                                : 'Defina data e horário para visualizar.'}
                            {employee?.value && !conflictingAppointment && !isEmployeeDayOff && !isEmployeeOnVacation && scheduledStartDate && scheduledEndDate && (
                                <div className="mt-2">
                                    {isCheckingAvailability
                                        ? 'Conferindo disponibilidade do profissional...'
                                        : 'Nenhum conflito encontrado na agenda deste profissional para esse intervalo.'}
                                </div>
                            )}
                            {isEmployeeOnVacation && (
                                <div className="mt-2">
                                    <i className="fa-solid fa-umbrella-beach me-1" aria-hidden="true"></i>
                                    {vacationMessage(employee, employee.label)} Escolha outra data ou outro profissional.
                                </div>
                            )}
                            {isEmployeeDayOff && !isEmployeeOnVacation && (
                                <div className="mt-2">
                                    {employee.label} não atende neste dia da semana. Escolha outra data ou outro profissional.
                                </div>
                            )}
                            {conflictingAppointment && (
                                <div className="mt-2">
                                    Já existe um atendimento deste profissional entre {formatTimeLabel(getAppointmentStartDate(conflictingAppointment))} e {formatTimeLabel(getAppointmentEndDate(conflictingAppointment))}.
                                    {isEncaixe
                                        ? ' O agendamento será salvo como encaixe, por cima desse horário.'
                                        : ' Ajuste o horário ou marque "Encaixe" para agendar mesmo assim.'}
                                </div>
                            )}
                        </Alert>

                        {/* Preço do clube neste dia. Fica junto do intervalo porque é a mesma
                            decisão: o operador está olhando a data escolhida. */}
                        <PlanOffDayNotice
                            customerId={customer?.value}
                            date={scheduledStartDate}
                            serviceId={service?.value}
                        />

                        {conflictingAppointment && !isReadOnly && (
                            <Form.Check
                                type="checkbox"
                                id="appointment-encaixe"
                                className="mt-2"
                                label="Encaixe — agendar neste horário mesmo com atendimento em paralelo"
                                checked={isEncaixe}
                                onChange={(event) => setIsEncaixe(event.target.checked)}
                            />
                        )}
                    </div>

                    <div className="appointment-form__panel form-layout__panel">
                        <div className="form-layout__panel-header">
                            <div>
                                <h5 className="mb-1">Atendimento</h5>
                                <p className="mb-0">Associe o serviço, profissional responsável e orientações complementares.</p>
                            </div>
                        </div>

                        <Row className="g-3">
                            <Col xl={6} md={12}>
                                <FormLabel>{t('service')}</FormLabel>
                                <ProductSelect
                                    value={service}
                                    type="SERVICE"
                                    onChange={(item) => {
                                        setService(item);
                                        // O tempo cadastrado do serviço preenche a duração, que continua
                                        // editável: o atendente pode alongar um atendimento específico
                                        // sem alterar o cadastro.
                                        if (item?.duration) {
                                            setDurationMinutes(String(item.duration));
                                        }
                                    }}
                                    isClearable={true}
                                    isDisabled={isReadOnly}
                                />
                            </Col>

                            <Col xl={6} md={12}>
                                <FormLabel>{t('employee')}</FormLabel>
                                <EmployeeSelect
                                    branch={branch}
                                    value={employee}
                                    onChange={(item) => setEmployee(item)}
                                    isClearable={true}
                                    isDisabled={isReadOnly}
                                />
                            </Col>

                            {!isEditing && (
                                <Col md={12}>
                                    <AppointmentDepositDraftSection
                                        draft={deposit}
                                        onChange={setDeposit}
                                        serviceOptions={depositServiceOptions}
                                        totalPrice={Number(convertStringToStringDouble(price) || 0)} />
                                </Col>
                            )}

                            <Col md={12}>
                                <FormLabel>{t('notes')}</FormLabel>
                                <TextArea
                                    value={notes}
                                    disabled={isReadOnly}
                                    onChange={(event) => setNotes(event.target.value)}
                                    maxLength={255}
                                    style={{ height: '96px' }}
                                    counter="true"
                                />
                            </Col>
                        </Row>
                    </div>

                    {appointmentStatus?.value === AppointmentStatus.CANCELED && (
                        <div className="appointment-form__panel form-layout__panel appointment-form__panel--danger">
                            <div className="form-layout__panel-header">
                                <div>
                                    <h5 className="mb-1">Motivo do cancelamento</h5>
                                    <p className="mb-0">Esse campo é obrigatório quando o status do agendamento for cancelado.</p>
                                </div>
                            </div>

                            <Row>
                                <Col md={12}>
                                    <FormLabel>{t('cancellation_reason')} <AsteriskRequired /></FormLabel>
                                    <TextArea
                                        value={cancellationReason}
                                        disabled={isReadOnly}
                                        onChange={(event) => setCancellationReason(event.target.value)}
                                        placeholder={t('cancellation_reason_placeholder')}
                                        maxLength={255}
                                        style={{ height: '84px' }}
                                        counter="true"
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </Col>

                <Col xl={4} lg={5}>
                    <div className="appointment-form__summary form-layout__panel">
                        <div className="appointment-form__summary-top">
                            <span className={`appointment-form__status-badge ${summaryStatusClass}`}>
                                {appointmentStatus?.label || 'Agendado'}
                            </span>
                            {uuid && (
                                <span className="appointment-form__summary-uuid">
                                    {uuid}
                                </span>
                            )}
                        </div>

                        <h5 className="appointment-form__summary-title">Resumo rápido</h5>
                        <p className="appointment-form__summary-copy">
                            Revise as informações principais antes de salvar para evitar retrabalho na agenda.
                        </p>

                        <div className="appointment-form__summary-list">
                            <div className="appointment-form__summary-item">
                                <span>Cliente</span>
                                <strong>{getSummaryValue(customer, 'Selecione um cliente')}</strong>
                            </div>

                            <div className="appointment-form__summary-item">
                                <span>Filial</span>
                                <strong>{getSummaryValue(branch, 'Selecione uma filial')}</strong>
                            </div>

                            <div className="appointment-form__summary-item">
                                <span>Quando</span>
                                <strong>{formatPreviewDateTime(appointmentDate, appointmentTime)}</strong>
                            </div>

                            <div className="appointment-form__summary-item">
                                <span>Serviço</span>
                                <strong>{getSummaryValue(service, 'Serviço ainda não definido')}</strong>
                            </div>

                            <div className="appointment-form__summary-item">
                                <span>Profissional</span>
                                <strong>{getSummaryValue(employee, 'Não atribuído')}</strong>
                            </div>

                            <div className="appointment-form__summary-item">
                                <span>Duração</span>
                                <strong>{`${effectiveDurationMinutes} min${normalizedDurationMinutes ? '' : ' (padrão)'}`}</strong>
                            </div>

                            <div className="appointment-form__summary-item">
                                <span>Preço</span>
                                <strong>{formatPreviewPrice(price)}</strong>
                            </div>
                        </div>

                        <div className="appointment-form__summary-note">
                            <i className="fa fa-info-circle"></i>
                            <span>
                                {isEditing
                                    ? 'O status não muda por aqui: confirmar, iniciar, finalizar e cancelar têm ação própria na agenda.'
                                    : 'Você pode criar o agendamento com o mínimo necessário e complementar os detalhes depois.'}
                            </span>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    ), [
        t, isEditing, isReadOnly, customer, branch, selectedBranch, appointmentStatus, uuid,
        appointmentDate, appointmentTime, service, employee, price, durationMinutes,
        notes, cancellationReason, summaryStatusClass, conflictingAppointment, isEmployeeDayOff, isEmployeeOnVacation,
        scheduledStartDate, scheduledEndDate, effectiveDurationMinutes,
        normalizedDurationMinutes, isCheckingAvailability, isEncaixe, deposit, depositServiceOptions
    ]);

    return (
        <NewPage
            motherMenu={t('appointments')}
            childMenu={t('appointments')}
            activeMenu={isEditing ? 'Visualizar' : t('new')}
            child={appointmentFormContent}
            handleSubmit={handleSubmit}
            cancelRoute={MenuConfig.appointment.appointment.route}
            isSubmitting={showLoading}
            hideSaveButton={isReadOnly}
        />
    );
};

export default AppointmentNew;
